import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getCustomSetting } from '@utils/customSettings';
import {
  SchedulerContextData,
  SchedulerData,
  SchedulerProviderProps,
  SchedulerSegment,
} from './types';
import { initialState, START_HOUR, HOURS_COUNT } from './constants';
import { groupSchedules } from '@components/scheduler/grid/helpers';
import { roundSchedulesDates } from './helpers';
import { useAppContext } from '@contexts/app';
import { getTenantScheduleHours } from '@utils/schedule/tenantHours';

const SchedulerContext = createContext<SchedulerContextData | Record<string, never>>({});

const HOURS_DEFAULTS = { dayStart: START_HOUR, dayEnd: START_HOUR + HOURS_COUNT };

export const SchedulerProvider = ({ children }: SchedulerProviderProps) => {
  const [state, setState] = useState<SchedulerData>(initialState);
  const { tenant } = useAppContext();
  const pointerRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const updatePointer = (x: number, y: number) => {
      pointerRef.current = { x, y };
    };
    const handlePointerMove = (e: PointerEvent) => updatePointer(e.clientX, e.clientY);
    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (touch) updatePointer(touch.clientX, touch.clientY);
    };
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const ribbonIntervals = (getCustomSetting(tenant, 'ribbon_intervals') ||
    initialState.segment.length) as number;

  const segment = useMemo<SchedulerSegment>(
    () => ({
      length: ribbonIntervals,
      count: 60 / ribbonIntervals,
      width: `calc(100%/${60 / ribbonIntervals})`,
    }),
    [ribbonIntervals],
  );

  const {
    data: { raw },
    hours,
    showFullDay,
  } = state;

  const { dayStart, hoursCount } = useMemo(
    () => getTenantScheduleHours(tenant, HOURS_DEFAULTS, showFullDay),
    [tenant, showFullDay],
  );

  useEffect(() => {
    setState((prev) => ({ ...prev, segment, dayStart, hoursCount }));
  }, [segment, dayStart, hoursCount]);

  const grouped = useMemo(
    () => ({
      foremen: raw.foremen.map((el) => ({
        ...el,
        schedules: groupSchedules(roundSchedulesDates(el.schedules, segment, hours)),
      })),
      withoutTeam: groupSchedules(roundSchedulesDates(raw.withoutTeam, segment, hours)),
    }),
    [raw, hours, segment],
  );

  return (
    <SchedulerContext.Provider
      value={{ ...state, data: { ...state.data, grouped }, setState, pointerRef }}>
      {children}
    </SchedulerContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSchedulerContext = () => useContext(SchedulerContext);
